import { useMemo, useState } from 'react';

/**
 * Lightweight dependency-free SVG line chart for a single time series.
 *
 * Props:
 *   points    : array of { label: string, value: number|null }
 *   color     : stroke/fill color for the line
 *   unit      : unit suffix shown on the Y axis and tooltip (e.g. "%", "MiB")
 *   yMin      : optional fixed lower bound for the Y axis (auto otherwise)
 *   yMax      : optional fixed upper bound for the Y axis (auto otherwise)
 *   height    : chart height in px (default 320)
 *   formatY   : optional fn(value) -> string for axis/tooltip labels
 */
function MetricLineChart({
  points,
  color = '#3498ff',
  unit = '',
  yMin,
  yMax,
  height = 320,
  formatY,
}) {
  const [hover, setHover] = useState(null); // index of hovered point

  const fmt = useMemo(
    () => formatY || ((v) => `${Math.round(v * 100) / 100}${unit ? ' ' + unit : ''}`),
    [formatY, unit]
  );

  // viewBox dimensions (chart scales to container width via width="100%")
  const W = 800;
  const H = height;
  const PAD_L = 64;
  const PAD_R = 24;
  const PAD_T = 20;
  const PAD_B = 48;
  const plotW = W - PAD_L - PAD_R;
  const plotH = H - PAD_T - PAD_B;

  const valued = useMemo(
    () => points.filter((p) => p.value !== null && p.value !== undefined && !Number.isNaN(p.value)),
    [points]
  );

  const { lo, hi } = useMemo(() => {
    if (valued.length === 0) return { lo: 0, hi: 1 };
    let mn = yMin !== undefined ? yMin : Math.min(...valued.map((p) => p.value));
    let mx = yMax !== undefined ? yMax : Math.max(...valued.map((p) => p.value));
    if (mn === mx) {
      // flat line — pad so it doesn't sit on an axis edge
      mn = mn === 0 ? 0 : mn * 0.9;
      mx = mx === 0 ? 1 : mx * 1.1;
    } else if (yMin === undefined && yMax === undefined) {
      const span = mx - mn;
      mn -= span * 0.08;
      mx += span * 0.08;
    }
    return { lo: mn, hi: mx };
  }, [valued, yMin, yMax]);

  const n = points.length;
  const xOf = (i) => PAD_L + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const yOf = (v) => PAD_T + plotH - ((v - lo) / (hi - lo || 1)) * plotH;

  // Build the polyline path, skipping null gaps.
  const path = useMemo(() => {
    let d = '';
    let pen = false;
    points.forEach((p, i) => {
      if (p.value === null || p.value === undefined || Number.isNaN(p.value)) {
        pen = false;
        return;
      }
      const cmd = pen ? 'L' : 'M';
      d += `${cmd}${xOf(i).toFixed(1)},${yOf(p.value).toFixed(1)} `;
      pen = true;
    });
    return d.trim();
  }, [points, lo, hi, n]);

  // Y axis gridlines (5 ticks).
  const yTicks = useMemo(() => {
    const ticks = [];
    const steps = 4;
    for (let i = 0; i <= steps; i++) {
      ticks.push(lo + ((hi - lo) * i) / steps);
    }
    return ticks;
  }, [lo, hi]);

  // X axis labels — show at most ~6 to avoid crowding.
  const xLabelIdx = useMemo(() => {
    if (n <= 6) return points.map((_, i) => i);
    const out = [];
    const stride = Math.ceil(n / 6);
    for (let i = 0; i < n; i += stride) out.push(i);
    if (out[out.length - 1] !== n - 1) out.push(n - 1);
    return out;
  }, [n, points]);

  if (valued.length === 0) {
    return (
      <div style={{ color: '#999', padding: 24, textAlign: 'center' }}>
        No data for this metric.
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={H}
        preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block' }}
      >
        {/* Y gridlines + labels */}
        {yTicks.map((t, i) => {
          const y = yOf(t);
          return (
            <g key={`y${i}`}>
              <line x1={PAD_L} y1={y} x2={W - PAD_R} y2={y} stroke="#3a3a3a" strokeWidth={1} />
              <text x={PAD_L - 8} y={y + 4} textAnchor="end" fontSize={12} fill="#aaa">
                {fmt(t)}
              </text>
            </g>
          );
        })}

        {/* X axis labels */}
        {xLabelIdx.map((i) => (
          <text
            key={`x${i}`}
            x={xOf(i)}
            y={H - PAD_B + 20}
            textAnchor="middle"
            fontSize={11}
            fill="#aaa"
          >
            {points[i].label}
          </text>
        ))}

        {/* area under line */}
        {path && (
          <path
            d={`${path} L${xOf(lastValuedIndex(points)).toFixed(1)},${(PAD_T + plotH).toFixed(1)} L${xOf(firstValuedIndex(points)).toFixed(1)},${(PAD_T + plotH).toFixed(1)} Z`}
            fill={color}
            opacity={0.08}
          />
        )}

        {/* line */}
        <path d={path} fill="none" stroke={color} strokeWidth={2} />

        {/* points + hover targets */}
        {points.map((p, i) => {
          if (p.value === null || p.value === undefined || Number.isNaN(p.value)) return null;
          const cx = xOf(i);
          const cy = yOf(p.value);
          const isHover = hover === i;
          return (
            <g key={`pt${i}`}>
              <circle cx={cx} cy={cy} r={isHover ? 5 : 3} fill={color} />
              {/* invisible wide hit area */}
              <rect
                x={cx - plotW / (2 * Math.max(n - 1, 1))}
                y={PAD_T}
                width={plotW / Math.max(n - 1, 1)}
                height={plotH}
                fill="transparent"
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover((h) => (h === i ? null : h))}
              />
            </g>
          );
        })}

        {/* tooltip */}
        {hover !== null && points[hover] && points[hover].value !== null && (
          <g pointerEvents="none">
            {(() => {
              const cx = xOf(hover);
              const cy = yOf(points[hover].value);
              const label = `${fmt(points[hover].value)}`;
              const sub = points[hover].label;
              const boxW = Math.max(label.length, sub.length) * 7 + 16;
              const boxH = 38;
              let bx = cx + 10;
              if (bx + boxW > W - PAD_R) bx = cx - boxW - 10;
              const by = Math.max(PAD_T, cy - boxH - 8);
              return (
                <>
                  <line x1={cx} y1={PAD_T} x2={cx} y2={PAD_T + plotH} stroke={color} strokeDasharray="3 3" opacity={0.5} />
                  <rect x={bx} y={by} width={boxW} height={boxH} rx={4} fill="#1a1a1a" stroke="#444" />
                  <text x={bx + 8} y={by + 16} fontSize={12} fill="#fff" fontWeight="bold">{label}</text>
                  <text x={bx + 8} y={by + 31} fontSize={10} fill="#aaa">{sub}</text>
                </>
              );
            })()}
          </g>
        )}
      </svg>
    </div>
  );
}

function firstValuedIndex(points) {
  for (let i = 0; i < points.length; i++) {
    const v = points[i].value;
    if (v !== null && v !== undefined && !Number.isNaN(v)) return i;
  }
  return 0;
}

function lastValuedIndex(points) {
  for (let i = points.length - 1; i >= 0; i--) {
    const v = points[i].value;
    if (v !== null && v !== undefined && !Number.isNaN(v)) return i;
  }
  return points.length - 1;
}

export default MetricLineChart;
